import { useState } from 'react';
import { GENERATORS, JAVASCRIPT, PYTHON, languageName, pythonWarnings, type ScriptPack, type ScriptPackRead } from '@realmery/realm-core';

/**
 * A script pack's import preview, as the desktop's: every generator and script it holds, each
 * one's whole source to read before anything is saved, the desktop's warnings about Python it
 * can't run, and the reader's notes. Importing runs nothing.
 */
export function PackImport({ read, from, onImport, onCancel }: { read: ScriptPackRead; from: string; onImport: (pack: ScriptPack) => Promise<void>; onCancel: () => void }) {
  const items = [
    ...read.pack.generators.map((g, i) => ({ id: `g${i}`, label: `The ${GENERATORS[g.key].name} generator (the desktop's, in Python)`, source: g.source, language: PYTHON, generator: g, script: null })),
    ...read.pack.scripts.map((s, i) => ({ id: `s${i}`, label: s.name, source: s.source, language: s.language, generator: null, script: s })),
  ];
  const [chosen, setChosen] = useState<Set<string>>(() => new Set(items.map((item) => item.id)));
  const [shown, setShown] = useState(items[0]?.id ?? '');
  const [working, setWorking] = useState(false);
  const item = items.find((i) => i.id === shown);

  async function confirm() {
    setWorking(true);
    await onImport({
      generators: items.filter((i) => i.generator && chosen.has(i.id)).map((i) => i.generator!),
      scripts: items.filter((i) => i.script && chosen.has(i.id)).map((i) => i.script!),
    });
    setWorking(false);
  }

  return (
    <section className="panel pack-import" aria-label="Import scripts">
      <h2>Import from {from}</h2>
      <p className="meta">Read each one before importing: nothing runs until you press Run.</p>
      <div className="pack-import-body">
        <ul className="pack-items">
          {items.map((i) => (
            <li key={i.id}>
              <label className="check">
                <input
                  type="checkbox"
                  checked={chosen.has(i.id)}
                  onChange={() =>
                    setChosen((all) => {
                      const next = new Set(all);
                      if (!next.delete(i.id)) next.add(i.id);
                      return next;
                    })
                  }
                />
                <span className="visually-hidden">Import</span>
              </label>
              <button type="button" className={i.id === shown ? 'outline-item selected' : 'outline-item'} aria-current={i.id === shown || undefined} onClick={() => setShown(i.id)}>
                {i.label}
                {(i.language === PYTHON ? pythonWarnings(i.source).length > 0 : false) && ' ⚠'}
              </button>
            </li>
          ))}
        </ul>
        {item && (
          <div className="pack-source">
            {item.script?.description && <p>{item.script.description}</p>}
            {item.generator ? (
              <p className="import-note">The desktop app will run this version of the generator; here the built-in one runs, and this is kept as it is.</p>
            ) : item.language === JAVASCRIPT ? (
              <p className="import-note">JavaScript: it runs here, in the sandbox.</p>
            ) : (
              <p className="import-note">{languageName(item.language)}: it's kept as it is, and runs in the desktop app; here it can be read.</p>
            )}
            {item.language === PYTHON &&
              pythonWarnings(item.source).map((warning) => (
                <p key={warning} className="notice-error">
                  ⚠ {warning}
                </p>
              ))}
            <pre className="code-view" aria-label={`The source of ${item.label}`}>
              {item.source}
            </pre>
          </div>
        )}
      </div>
      {read.notes.length > 0 && (
        <ul className="import-notes">
          {read.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      )}
      <div className="actions">
        <button type="button" className="primary" disabled={chosen.size === 0 || working} onClick={() => void confirm()}>
          Import {chosen.size}
        </button>
        <button type="button" onClick={onCancel} disabled={working}>
          Cancel
        </button>
      </div>
    </section>
  );
}
